import type { DayHours } from "./day-hours.type";

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Reads the stored text of a week ("1=09:00-12:00,14:00-18:00;3=09:00-17:00": a day, then up to two ranges) into days. Empty text is a week with nothing; null when anything is wrong (a day twice or outside 1-7, a time not "HH:MM", a range that ends before it starts, more than two ranges, ranges overlapping). */
export function parseWeeklyHours(text: string): DayHours[] | null {
  if (text === "") return [];
  const days: DayHours[] = [];
  for (const part of text.split(";")) {
    const [d, rest] = part.split("=");
    const day = Number(d);
    const ranges = (rest ?? "").split(",").map((r) => ({ from: r.split("-")[0] ?? "", to: r.split("-")[1] ?? "" }));
    const bad = ranges.length > 2 || ranges.some((r) => !TIME.test(r.from) || !TIME.test(r.to) || r.from >= r.to) || (ranges[1] !== undefined && ranges[0].to > ranges[1].from);
    if (!rest || !Number.isInteger(day) || day < 1 || day > 7 || days.some((x) => x.day === day) || bad) return null;
    days.push({ day, ranges });
  }
  return days;
}

/** Writes days as the stored text of a week (see `parseWeeklyHours`), in day order, leaving out days with no range. */
export function formatWeeklyHours(days: DayHours[]): string {
  return [...days].sort((a, b) => a.day - b.day).filter((d) => d.ranges.length > 0).map((d) => `${d.day}=${d.ranges.map((r) => `${r.from}-${r.to}`).join(",")}`).join(";");
}

/** Checks the text of a week from a form and returns it tidied (days in order, empty days gone), or null when it is not a valid week. Empty is valid. */
export function verifyWeeklyHours(text: string): string | null {
  const days = parseWeeklyHours(text.trim());
  return days ? formatWeeklyHours(days) : null;
}
